import Cookies from 'js-cookie'
import type { LoginCredentials, RegisterCredentials } from '@/types/auth';
import { jwtDecode } from 'jwt-decode'; // 引入解碼函式
// import { api } from '@/services';
import {api} from '../api' // axios instance
import { LoginResponse, API_ENDPOINTS, JwtClaims } from '@myorg/type-lib';
import { logger } from '@myorg/type-lib';
import { redirectToClientLogin } from '@/auth/redirect/authRedirect';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { STORAGE_KEYS } from '@/config/storageKeys';

export async function login(data: LoginCredentials): Promise<LoginResponse> {
  logger.log('[authApi.login] ===== START =====');
  const apiInstance = api();
  
  // 💡 直接從目前的網址列暴力撈出最新的 csrf（不依賴全域變數）
  const urlParams = new URLSearchParams(window.location.search);
  const liveCsrfToken = urlParams.get('csrf') || '';

  logger.log('%c[必殺技] 單次請求強行注入的 Token:', 'color: red; font-size: 14px;', liveCsrfToken);

  const res: LoginResponse = await apiInstance.post(
    API_ENDPOINTS.ACCOUNT.LOGIN,
    { ...data }, {
    // 💡 關鍵：寫在單次請求的第三個參數 (config) 裡，這通常具有最高優先權，不易被 defaults 覆蓋
    headers: {
      'X-XSRF-TOKEN': liveCsrfToken,
      'X-CSRF-Token': liveCsrfToken
    }}
  );

  logger.log('[authApi.login] Login API response:', res.data);

  // 2. 拿到我們在 Go 裡面算好的 target 網址
    const redirectUrl = res.data?.redirect_url

    if (redirectUrl) {
      logger.log('🎉 登入驗證成功！立刻切斷前端請求，執行大跳轉:', redirectUrl)

      // 💡 關鍵：直接用原生網址跳轉，這會讓瀏覽器重新整理並跑完 OAuth2 後續流程
      // 如果是 OAuth2 authorize 端點，使用 window.location.href 觸發完整頁面重載
      if (redirectUrl.includes('/oauth2/authorize')) {
        logger.log('[authApi.login] Redirecting to OAuth authorize endpoint');
        window.location.href = redirectUrl
        return res
      }

      // For non-OAuth redirect, the session cookie is set by the login response
      // We need to redirect first, then handleAuthSuccess will be called after the page loads
      // via router guard or onMounted
      logger.log('[authApi.login] Non-OAuth redirect, will call handleAuthSuccess after redirect');
      // Don't call handleAuthSuccess here - let it be called after redirect when session is established
      return res;
    }

  logger.log('[authApi.login] No redirect URL, calling handleAuthSuccess');
  await handleAuthSuccess();

  logger.log('[authApi.login] ===== END =====');
  return res;
}

// 定義 Captcha 的回傳型別
export interface CaptchaInitData {
  captchaId: string;
  bg: string;
  slice: string;
  target: number;
  y: number;
}

/**
 * 初始化驗證碼目標
 */
export const initCaptcha = async (): Promise<CaptchaInitData | undefined> => {
  try {
    // 注意：使用 api() 通常已經處理過 response 攔截，直接拿 data
    const res = await api().get('/api/go-bff-service/public/captcha/init');

    if (res && res.data && res.data.data) {
      return res.data.data as CaptchaInitData;
    }

    return undefined;
  } catch (err) {
    logger.error("驗證碼初始化失敗", err);
    throw err; // 拋出錯誤讓組件能處理 UI 狀態
  }
};

export const logout = async () => {
  logger.log('[api.auth.logout] Starting API logout');
  try {
    // 注意：使用 api() 通常已經處理過 response 攔截，直接拿 data
    const res = await api().post('/api/client/logout');
    logger.log('[api.auth.logout] API call successful, redirecting to login');
    redirectToClientLogin()

  } catch (err) {
    logger.error('[api.auth.logout] 登出失敗:', err);
  } finally {
    logger.log('[api.auth.logout] API logout function completed');
  }
};

export async function register(
  data: RegisterCredentials
): Promise<LoginResponse> {
  const res: LoginResponse = await api().post(
    API_ENDPOINTS.ACCOUNT.REGISTER,
    { ...data }
  );

  await handleAuthSuccess();

  return res;
}

export async function handleAuthSuccess() {
  logger.log('[handleAuthSuccess] ===== START =====');
  let userId, merchantId, name, rolesArray;

  // Check if we're in native app mode with OAuth tokens
  if (Capacitor.isNativePlatform()) {
    logger.log('[handleAuthSuccess] Mode: Native/OAuth');
    const { value: accessToken } = await Preferences.get({ key: 'access_token' });
    logger.log('[handleAuthSuccess] Access token exists:', !!accessToken);
    if (accessToken) {
      // Decode JWT token to get user info
      const decodedToken: any = jwtDecode(accessToken);
      userId = decodedToken.userId || decodedToken.sub || '';
      merchantId = decodedToken.merchantId || '';
      name = decodedToken.username || decodedToken.name || decodedToken.sub || '';
      rolesArray = decodedToken.roles || [];
      logger.log('[handleAuthSuccess] OAuth mode: decoded from JWT', { userId, merchantId, name, rolesArray });
    } else {
      throw new Error('No access token found in Preferences');
    }
  } else {
    // Web mode: call API
    logger.log('[handleAuthSuccess] Mode: Web/API');
    const res = await api().get(API_ENDPOINTS.CLIENT.ME);
    const userData = res.data;
    logger.log('[handleAuthSuccess] API response data:', userData);

    // 防禦 1：確保 userData 存在
    if (!userData) {
      throw new Error('後端回傳資料為空 (Empty User Data)');
    }

    userId = userData.userId;
    merchantId = userData.merchantId;
    name = userData.name;
    const roles = userData.roles;

    // 防禦 2：安全地處理 roles，如果不存在或不是字串，就給個預設陣列
    if (typeof roles === 'string' && roles.trim() !== '') {
      rolesArray = roles.split(",");
    } else if (Array.isArray(roles)) {
      rolesArray = roles; // 如果後端哪天改成直接給陣列，相容它
    } else {
      rolesArray = [];
    }
    logger.log('[handleAuthSuccess] Web mode: from API', { userId, merchantId, name, rolesArray });
  }

  const jsonString = JSON.stringify(rolesArray);
  logger.log('[handleAuthSuccess] About to write to localStorage:', { userId, merchantId, name, rolesArray });

  // 防禦 3：確保有值才塞進 localStorage，避免塞入 "undefined" 字串
  if (userId) {
    localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
    logger.log('[handleAuthSuccess] Wrote USER_ID:', userId);
  } else {
    logger.warn('[handleAuthSuccess] Skipping USER_ID - empty');
  }
  if (merchantId) {
    localStorage.setItem(STORAGE_KEYS.MERCHANT_ID, merchantId);
    logger.log('[handleAuthSuccess] Wrote MERCHANT_ID:', merchantId);
  } else {
    logger.warn('[handleAuthSuccess] Skipping MERCHANT_ID - empty');
  }
  if (name) {
    localStorage.setItem(STORAGE_KEYS.USERNAME, name);
    logger.log('[handleAuthSuccess] Wrote USERNAME:', name);
  } else {
    logger.warn('[handleAuthSuccess] Skipping USERNAME - empty');
  }
  localStorage.setItem(STORAGE_KEYS.USER_ROLES, jsonString);
  logger.log('[handleAuthSuccess] Wrote USER_ROLES:', jsonString);

  logger.log('[handleAuthSuccess] localStorage after storing:', {
    userId: localStorage.getItem(STORAGE_KEYS.USER_ID),
    merchantId: localStorage.getItem(STORAGE_KEYS.MERCHANT_ID),
    username: localStorage.getItem(STORAGE_KEYS.USERNAME),
    userRoles: localStorage.getItem(STORAGE_KEYS.USER_ROLES)
  });
  logger.log('[handleAuthSuccess] ===== END =====');
}